import { Fade } from "@mui/material"
import { LinkOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import React, { useState } from "react"

import useCompanyAccountingTerms from "../../hooks/useCompanyAccountingTerms"

import { PaymentTermsView } from "./PaymentTermsView"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import CircularLoadingIndicator from "@/components/common/LoadingIndicators/CircularLoadingIndicator"
import { usePaymentTermsContext } from "@/screens/CompanySettings/Accounting/contexts/PaymentTermsContext"
import { CompanyAccountingTermsDialog } from "@/screens/CompanySettings/Accounting/dialogs/CompanyAccountingTermsDialog"
import { useGetVendorPayableFields } from "@/screens/CompanySettings/Accounting/hooks/useGetVendorPayableFields"

enum ModalState {
  CREATE = "create",
  EDIT = "edit",
}

const PaymentTermsSection: FC<{ companyId: string }> = ({ companyId }) => {
  const { accountingTermsList, accountingTermsFetching, refetchAccountingTermsList } = useCompanyAccountingTerms({
    companyId,
  })
  const { isCompanyVendor, isCompanyCustomer } = usePaymentTermsContext()
  const [isModalOpened, setIsModalOpened] = useState<ModalState | null>(null)
  const {
    fields: vendorPayableFields,
    isFetching: isFetchingVendorPayableFields,
    refetch: refetchVendorPayableFields,
  } = useGetVendorPayableFields(companyId, !isCompanyVendor)

  const canOpenDialog = !isCompanyVendor || !isFetchingVendorPayableFields

  const handleDialogSubmit = (): void => {
    refetchAccountingTermsList()
    refetchVendorPayableFields()
    setIsModalOpened(null)
  }

  const noDataTitle = getNoDataTitle(isCompanyVendor, isCompanyCustomer)

  const hasData = accountingTermsList.length > 0
  const fetchingData = accountingTermsFetching && !hasData
  const noData = accountingTermsList.length === 0 && !accountingTermsFetching

  return (
    <>
      {hasData && (
        <PaymentTermsView
          accountingTermsList={accountingTermsList}
          vendorPayableFields={vendorPayableFields}
          onOpenDialog={() => {
            setIsModalOpened(ModalState.EDIT)
          }}
        />
      )}
      {fetchingData && <CircularLoadingIndicator />}
      {noData && (
        <Fade in={true} unmountOnExit timeout={500}>
          <InformationMissingAlert
            title={noDataTitle}
            titleClassName='text-ml'
            action={
              <Button
                color='inherit'
                size='small'
                appearance='outlined'
                className='text-primary'
                startIcon={<LinkOutlined />}
                onClick={() => setIsModalOpened(ModalState.CREATE)}
              >
                Add Details
              </Button>
            }
          />
        </Fade>
      )}
      {isModalOpened && canOpenDialog && (
        <CompanyAccountingTermsDialog
          companyId={companyId}
          vendorPayableFields={vendorPayableFields}
          onSubmit={handleDialogSubmit}
          data={isModalOpened === ModalState.EDIT ? accountingTermsList : undefined}
          onClose={() => setIsModalOpened(null)}
        />
      )}
    </>
  )
}

export default PaymentTermsSection

function getNoDataTitle(isVendor: boolean, isCustomer: boolean): string {
  if (isVendor && !isCustomer) {
    return "Add company’s lending payment terms and credit limits"
  }
  if (isCustomer && !isVendor) {
    return "Add company’s borrowing payment terms and credit limits"
  }
  return "Add company’s lending and borrowing payment terms and credit limits"
}
